import { Picture } from "~/components/ui/Picture";
import Header from "~/components/ui/SectionHeader";
import type { ImageWidget } from "~/types/widgets";

/**
 * @titleBy alt
 */
export interface Banner {
  srcMobile: ImageWidget;
  srcDesktop?: ImageWidget;
  /**
   * @description Image alt text
   */
  alt: string;
  /**
   * @description Adicione um link
   */
  href: string;
}

export type BorderRadius =
  | "none"
  | "sm"
  | "md"
  | "lg"
  | "xl"
  | "2xl"
  | "3xl"
  | "full";

export interface Props {
  title?: string;
  description?: string;
  /**
   * @maxItems 4
   * @minItems 4
   */
  banners?: Banner[];
  layout?: {
    /**
     * @description Aplique borda a sua imagem
     */
    borderRadius?: {
      /** @default none */
      mobile?: BorderRadius;
      /** @default none */
      desktop?: BorderRadius;
    };
    headerAlignment?: "center" | "left";
    mobile?: "Asymmetric" | "Symmetrical";
    desktop?: "Asymmetric" | "Symmetrical";
  };
}

const RADIUS: Record<string, Record<BorderRadius, string>> = {
  mobile: {
    none: "rounded-none",
    sm: "rounded-sm",
    md: "rounded-md",
    lg: "rounded-lg",
    xl: "rounded-xl",
    "2xl": "rounded-2xl",
    "3xl": "rounded-3xl",
    full: "rounded-full",
  },
  desktop: {
    none: "sm:rounded-none",
    sm: "sm:rounded-sm",
    md: "sm:rounded-md",
    lg: "sm:rounded-lg",
    xl: "sm:rounded-xl",
    "2xl": "sm:rounded-2xl",
    "3xl": "sm:rounded-3xl",
    full: "sm:rounded-full",
  },
};

const DEFAULT_PROPS: Props = {
  banners: [
    {
      srcMobile:
        "https://decoims.com/danielatombini/905ce6e1-3fa3-4e45-9d48-3c278afa9938/b531631b_b531631b-8523-4feb-ac37-5112873abad2.jpg",
      srcDesktop:
        "https://decoims.com/danielatombini/905ce6e1-3fa3-4e45-9d48-3c278afa9938/b531631b_b531631b-8523-4feb-ac37-5112873abad2.jpg",
      alt: "Fashion",
      href: "/",
    },
    {
      alt: "Fashion",
      href: "/",
      srcMobile:
        "https://decoims.com/danielatombini/8b9477db-cabe-4261-ae55-c800a693521d/1125d938_1125d938-89ff-4aae-a354-63d4241394a6.jpg",
      srcDesktop:
        "https://decoims.com/danielatombini/8b9477db-cabe-4261-ae55-c800a693521d/1125d938_1125d938-89ff-4aae-a354-63d4241394a6.jpg",
    },
    {
      srcMobile:
        "https://decoims.com/danielatombini/40caddc8-4b6e-41df-8405-7048e12bba6f/dd1e2acb_dd1e2acb-ff80-49f9-8f56-1deac3b7a42d.jpg",
      srcDesktop:
        "https://decoims.com/danielatombini/40caddc8-4b6e-41df-8405-7048e12bba6f/dd1e2acb_dd1e2acb-ff80-49f9-8f56-1deac3b7a42d.jpg",
      href: "/",
      alt: "Fashion",
    },
    {
      srcMobile:
        "https://decoims.com/danielatombini/78caeec5-69a7-45f8-a262-3d7d2f92020e/0b85ba2d_0b85ba2d-48b1-4f5b-b619-7f4a7f50b455.jpg",
      srcDesktop:
        "https://decoims.com/danielatombini/78caeec5-69a7-45f8-a262-3d7d2f92020e/0b85ba2d_0b85ba2d-48b1-4f5b-b619-7f4a7f50b455.jpg",
      alt: "Fashion",
      href: "/",
    },
  ],
  layout: {
    borderRadius: {
      mobile: "3xl",
      desktop: "2xl",
    },
    headerAlignment: "center",
    mobile: "Asymmetric",
    desktop: "Asymmetric",
  },
};

function Banner(
  props: Banner & {
    borderRadius?: {
      /** @default none */
      mobile?: BorderRadius;
      /** @default none */
      desktop?: BorderRadius;
    };
  },
) {
  const { borderRadius, srcMobile, srcDesktop, alt } = props;
  const radiusDesktop = RADIUS.desktop[borderRadius?.desktop ?? "none"];
  const radiusMobile = RADIUS.mobile[borderRadius?.desktop ?? "none"];

  return (
    <a
      href={props.href}
      className={`overflow-hidden ${radiusDesktop} ${radiusMobile}`}
    >
      <Picture
        sources={[
          {
            width: 190,
            height: 190,
            media: "(max-width: 767px)",
            src: srcMobile,
          },
          {
            width: 508,
            height: 420,
            media: "(min-width: 768px)",
            src: srcDesktop || srcMobile,
          },
        ]}
        src={srcMobile}
        width={190}
        height={190}
        className="w-full h-full object-cover"
        alt={alt}
        decoding="async"
        loading="lazy"
      />

    </a>
  );
}

function Gallery(props: Props) {
  const { title, description, banners, layout } = {
    ...DEFAULT_PROPS,
    ...props,
  };

  const mobileItemLayout = (index: number) =>
    layout?.mobile === "Symmetrical"
      ? "row-span-3"
      : index === 0 || index === 3
      ? "row-span-3"
      : "row-span-2";

  const desktopItemLayout = (index: number) =>
    layout?.desktop === "Symmetrical"
      ? "sm:row-span-3"
      : index === 0 || index === 3
      ? "sm:row-span-3"
      : "sm:row-span-2";

  return (
    <section className="container px-4 py-8 flex flex-col gap-8 lg:gap-10 lg:py-10 lg:px-0">
      <Header
        title={title}
        description={description}
        alignment={layout?.headerAlignment || "center"}
      />
      <ul className="grid grid-flow-col grid-cols-2 grid-rows-6 gap-4 list-none">
        {banners?.map((banner, index) => (
          <li className={`${mobileItemLayout(index)} ${desktopItemLayout(index)}`}>
            <Banner {...banner} borderRadius={props.layout?.borderRadius} />
          </li>
        ))}
      </ul>
    </section>
  );
}

export default Gallery;

export const eager = true;
export const sync = true;
